import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PenLine, Type, Eraser, Rocket, CreditCard } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function SignatureCapture({ open, onClose, onConfirm, title, financial, paymentMethods = [] }) {
  const { user } = useAuth();
  const [mode, setMode] = useState("draw");
  const [typed, setTyped] = useState("");
  const [name, setName] = useState(user?.command_center_name || user?.email || "Commander");
  const [pmId, setPmId] = useState(paymentMethods[0]?.id || "");
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const lastPt = useRef(null);
  const emptyRef = useRef(true);

  useEffect(() => {
    if (!open) return;
    // Wait for the canvas to mount before configuring it.
    requestAnimationFrame(() => {
      const c = canvasRef.current;
      if (!c) return;
      const ctx = c.getContext("2d");
      ctx.fillStyle = "#0A0A0A";
      ctx.fillRect(0, 0, c.width, c.height);
      ctx.strokeStyle = "#F5F5F5";
      ctx.lineWidth = 2.4;
      ctx.lineCap = "round";
      emptyRef.current = true;
    });
  }, [open, mode]);

  const point = (e) => {
    const c = canvasRef.current;
    if (!c) return null;
    const r = c.getBoundingClientRect();
    const t = e.touches?.[0] || e;
    return { x: t.clientX - r.left, y: t.clientY - r.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    lastPt.current = point(e);
  };
  const move = (e) => {
    if (!drawing.current) return;
    const p = point(e);
    if (!p) return;
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(lastPt.current.x, lastPt.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPt.current = p;
    emptyRef.current = false;
  };
  const end = () => { drawing.current = false; };

  const clear = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#0A0A0A";
    ctx.fillRect(0, 0, c.width, c.height);
    emptyRef.current = true;
  };

  const buildTypedDataUrl = () => {
    const c = document.createElement("canvas");
    c.width = 600; c.height = 180;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#0A0A0A";
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.fillStyle = "#F5F5F5";
    ctx.font = "italic 44px 'Times New Roman', serif";
    ctx.textBaseline = "middle";
    ctx.fillText(typed || name || "Commander", 20, 90);
    return c.toDataURL("image/png");
  };

  const confirm = () => {
    let dataUrl = null;
    if (mode === "draw" && !emptyRef.current) {
      dataUrl = canvasRef.current.toDataURL("image/png");
    } else if (mode === "type" && typed.trim()) {
      dataUrl = buildTypedDataUrl();
    }
    onConfirm({ dataUrl, name: name || "Commander" }, financial ? pmId : null);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg rounded-none border border-white/15 bg-[#0A0A0A]" data-testid="signature-dialog">
        <DialogHeader>
          <div className="uc-chip mb-2 w-fit"><PenLine size={11}/> COMMANDER SIGNATURE</div>
          <DialogTitle className="font-serif-display text-2xl">{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex gap-2">
            <button data-testid="sig-mode-draw" onClick={() => setMode("draw")} className={`flex-1 h-9 rounded-none border font-mono-uc text-[11px] uppercase tracking-widest flex items-center justify-center gap-1 ${mode === "draw" ? "border-amber-500/50 bg-amber-500/10 text-amber-300" : "border-white/10 text-white/50 hover:bg-white/5"}`}>
              <PenLine size={11}/> Draw
            </button>
            <button data-testid="sig-mode-type" onClick={() => setMode("type")} className={`flex-1 h-9 rounded-none border font-mono-uc text-[11px] uppercase tracking-widest flex items-center justify-center gap-1 ${mode === "type" ? "border-amber-500/50 bg-amber-500/10 text-amber-300" : "border-white/10 text-white/50 hover:bg-white/5"}`}>
              <Type size={11}/> Type
            </button>
          </div>

          {mode === "draw" ? (
            <div className="relative">
              <canvas
                ref={canvasRef}
                width={480}
                height={160}
                className="w-full bg-[#0A0A0A] border border-white/10 touch-none"
                data-testid="signature-canvas"
                onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                onTouchStart={start} onTouchMove={move} onTouchEnd={end}
              />
              <button data-testid="signature-clear" onClick={clear} className="absolute top-2 right-2 font-mono-uc text-[10px] uppercase tracking-widest text-white/40 hover:text-red-300 flex items-center gap-1">
                <Eraser size={10}/> clear
              </button>
            </div>
          ) : (
            <Input
              data-testid="signature-typed"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder="Type your name — will render as script"
              className="h-11 rounded-none bg-black border-white/10 font-serif-display text-xl italic"
            />
          )}

          <div>
            <label className="font-mono-uc text-[10px] uppercase tracking-widest text-white/50 block mb-1">Name on Signature</label>
            <Input data-testid="signature-name" value={name} onChange={(e) => setName(e.target.value)}
                   className="h-9 rounded-none bg-black border-white/10 font-mono-uc text-sm"/>
          </div>

          {financial && (
            <div className="p-3 border border-teal-500/30 bg-teal-500/5">
              <label className="font-mono-uc text-[10px] uppercase tracking-widest text-teal-300 mb-2 flex items-center gap-1">
                <CreditCard size={11}/> Charge To
              </label>
              {paymentMethods.length === 0 ? (
                <div className="text-red-300 text-[11px]">No payment methods on file. Add one in Command Wallet.</div>
              ) : (
                <select
                  data-testid="signature-payment-method"
                  value={pmId}
                  onChange={(e) => setPmId(e.target.value)}
                  className="w-full h-9 rounded-none bg-black border border-white/10 font-mono-uc text-xs px-2"
                >
                  {paymentMethods.map((m) => (
                    <option key={m.id} value={m.id}>{m.label} ••{m.last4}</option>
                  ))}
                </select>
              )}
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <Button variant="ghost" onClick={onClose} className="flex-1 rounded-none h-10 border border-white/10 text-white/70">
              Cancel
            </Button>
            <Button data-testid="signature-confirm" onClick={confirm} className="flex-1 rounded-none h-10 uc-btn-amber">
              <Rocket size={12} className="mr-1"/> Approve & Execute
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
